import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowLeft, Package, Printer, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';
import PrintOrder from '@/components/PrintOrder';
import OrderSuccessModal from '@/components/OrderSuccessModal';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyOrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [duplicatedOrder, setDuplicatedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-neutral-200',
      shipped: 'bg-green-100 text-green-800 border-green-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return texts[status] || status;
  };

  const handleDuplicate = async (order) => {
    if (!window.confirm(`¿Duplicar el pedido ${order.order_number}? Se creará un pedido nuevo con las mismas líneas, listo para revisar y ajustar cantidades/fechas.`)) return;
    try {
      setDuplicatingId(order.id);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/orders/${order.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Pedido duplicado: ${data.order_number}`);
      setDuplicatedOrder(data);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar el pedido');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handlePrint = (order) => {
    setPrintingOrder(order);
    setTimeout(() => {
      window.print();
      setPrintingOrder(null);
    }, 100);
  };

  const handleDownloadExcel = (order) => {
    // Prepare data for Excel
    const excelData = [];
    let totalPares = 0;
    let totalEuros = 0;
    
    order.items.forEach(item => {
      totalPares += item.quantity;
      totalEuros += item.price * item.quantity;
      
      excelData.push({
        'Nº Pedido': order.order_number,
        'Fecha': new Date(order.created_at).toLocaleDateString('es-ES'),
        'Cliente': order.business_name,
        'Email': order.user_email,
        'Referencia': item.reference,
        'MARCA': 'MACRON',
        'Producto': item.model_name,
        'Color': item.color,
        'Talla': item.size,
        'EAN': item.ean || '-',
        'Cantidad': item.quantity,
        'Precio Unitario': item.price.toFixed(2),
        'Subtotal': (item.price * item.quantity).toFixed(2),
        'Estado': order.status === 'pending' ? 'Pendiente' : order.status === 'confirmed' ? 'Confirmado' : order.status,
        'Observaciones': order.observations || '-',
        'Fecha Entrega': order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('es-ES') : '-'
      });
    });

    // Add summary rows
    excelData.push({});
    excelData.push({
      'Nº Pedido': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total PARES',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': '',
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });
    excelData.push({});
    excelData.push({
      'Nº Pedido': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total Pedido',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': totalEuros.toFixed(2),
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 15 },
      { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 8 }, { wch: 15 },
      { wch: 10 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 30 }, { wch: 15 }
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pedido');

    // Generate filename
    const filename = `Pedido_${order.order_number}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
    toast.success('Excel descargado exitosamente');
  };

  // Group order items by reference/model/color
  const groupOrderItems = (items) => {
    const grouped = {};
    
    items.forEach(item => {
      const key = `${item.reference}-${item.model_name}-${item.color}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          reference: item.reference,
          model_name: item.model_name,
          color: item.color,
          ean: item.ean,
          sizes: [],
          totalPairs: 0,
          subtotal: 0
        };
      }
      
      grouped[key].sizes.push({
        size: item.size,
        quantity: item.quantity,
        price: item.price
      });
      grouped[key].totalPairs += item.quantity;
      grouped[key].subtotal += item.price * item.quantity;
    });
    
    return Object.values(grouped);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <ShoppingBag className="w-8 h-8 text-black" />
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-9" />
            </Link>
          </div>
        </div>
      </header>

      {/* Orders Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Mis Pedidos</h1>
          <p className="text-slate-600">Historial de pedidos realizados</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-slate-600 text-lg">Cargando pedidos...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No tienes pedidos aún</h2>
            <p className="text-slate-600 mb-6">Empieza a comprar en nuestro catálogo</p>
            <Link to="/catalog">
              <Button data-testid="go-to-catalog-btn" className="bg-black hover:bg-neutral-800">
                Ir al Catálogo
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div 
                key={order.id} 
                data-testid={`order-${order.id}`}
                className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
              >
                {/* Order Header */}
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-bold text-slate-900">Pedido #{order.order_number}</h3>
                        {order.macron_po_number && (
                          <span className="text-[10px] uppercase tracking-widest bg-emerald-50 border border-emerald-300 text-emerald-800 font-black px-2 py-1 rounded">
                            ✓ MACRON #{order.macron_po_number}
                          </span>
                        )}
                        {order.duplicated_from && (
                          <span className="text-[10px] uppercase tracking-widest bg-neutral-100 border border-neutral-300 text-neutral-600 px-2 py-1 rounded">
                            duplicado de {order.duplicated_from}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {new Date(order.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        onClick={() => handleDownloadExcel(order)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                      >
                        <Download className="w-4 h-4" />
                        Excel
                      </Button>
                      <Button
                        onClick={() => handlePrint(order)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Printer className="w-4 h-4" />
                        Imprimir
                      </Button>
                      <Button
                        data-testid={`duplicate-order-${order.order_number}`}
                        onClick={() => handleDuplicate(order)}
                        disabled={duplicatingId === order.id}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 bg-black text-white hover:bg-neutral-800 border-black disabled:opacity-40"
                      >
                        <Copy className="w-4 h-4" />
                        {duplicatingId === order.id ? 'Duplicando…' : 'Duplicar'}
                      </Button>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Items - Grouped by Reference */}
                <div className="p-6">
                  <div className="space-y-4 mb-4">
                    {groupOrderItems(order.items).map((group, index) => (
                      <div key={index} className="py-4 border-b border-slate-100 last:border-0">
                        <div className="mb-2">
                          <p className="font-bold text-slate-900 text-lg">{group.model_name}</p>
                          <p className="text-sm text-slate-600 font-semibold">
                            Referencia: {group.reference} | Color: {group.color}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-slate-700">
                              <span className="font-semibold">Tallas:</span>{' '}
                              {group.sizes
                                .sort((a, b) => parseFloat(a.size) - parseFloat(b.size))
                                .map(s => `${s.size}(${s.quantity})`)
                                .join(', ')}
                            </p>
                            <p className="text-sm text-black font-semibold mt-1">
                              Total: {group.totalPairs} pares
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="font-bold text-slate-900 text-lg">
                              €{group.subtotal.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Total */}
                  <div className="flex justify-between items-center pt-4 border-t-2 border-slate-200">
                    <span className="text-lg font-bold text-slate-900">Total</span>
                    <span className="text-2xl font-bold text-black">€{order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Print View - Hidden on screen, visible when printing */}
      {printingOrder && (
        <div className="hidden print:block">
          <PrintOrder order={printingOrder} />
        </div>
      )}

      {duplicatedOrder && (
        <OrderSuccessModal
          order={duplicatedOrder}
          savings={
            Math.max(
              0,
              (duplicatedOrder.items || []).reduce(
                (s, i) =>
                  s +
                  ((typeof i.list_price === 'number' && i.list_price > 0 ? i.list_price : i.price || 0) - (i.price || 0)) *
                    (i.quantity || 0),
                0
              )
            )
          }
          listTotal={
            (duplicatedOrder.items || []).reduce(
              (s, i) =>
                s +
                (typeof i.list_price === 'number' && i.list_price > 0 ? i.list_price : i.price || 0) *
                  (i.quantity || 0),
              0
            )
          }
          deliveryDate={duplicatedOrder.delivery_date}
          onContinueShopping={() => {
            setDuplicatedOrder(null);
            navigate('/catalog');
          }}
        />
      )}
    </div>
  );
};

export default MyOrders;
